import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, TrendingUp, Shield, ArrowLeft, Mail, Lock, KeyRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password states
  const [forgotMode, setForgotMode] = useState(null); // null | 'email' | 'code' | 'done'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async () => {
    if (!resetEmail) { toast.error('Enter your email'); return; }
    setResetLoading(true);
    try {
      await authAPI.forgotPassword(resetEmail);
      toast.success('Reset code sent! Check your email.');
      setForgotMode('code');
    } catch { toast.error('Something went wrong'); }
    setResetLoading(false);
  };

  const handleResetSubmit = async () => {
    if (!resetCode || !newPassword) { toast.error('Code and new password required'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResetLoading(true);
    try {
      await authAPI.resetPassword({ email: resetEmail, code: resetCode, new_password: newPassword });
      toast.success('Password reset! You can now sign in.');
      setForgotMode(null);
      setEmail(resetEmail);
      setPassword('');
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid or expired code'); }
    setResetLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <Link to="/" className="inline-flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#b8ff00] to-[#00ff88] flex items-center justify-center rounded-xl">
              <span className="font-display font-bold text-black text-xl">M</span>
            </div>
            <span className="font-display font-bold text-2xl">MMarketing</span>
          </Link>

          <AnimatePresence mode="wait">
            {!forgotMode ? (
              <motion.div key="login" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}>
                <h1 className="font-display font-bold text-3xl text-zinc-900 mb-2">Welcome back</h1>
                <p className="text-zinc-500 mb-8">Sign in to access your account</p>

                <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-700">Email</Label>
                    <Input id="email" name="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-12 bg-zinc-50 border-zinc-200 rounded-xl" required data-testid="login-email-input" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-zinc-700">Password</Label>
                      <button type="button" onClick={() => { setForgotMode('email'); setResetEmail(email); }} className="text-xs font-semibold text-[#00aa55] hover:underline" data-testid="forgot-password-link">
                        Forgot password?
                      </button>
                    </div>
                    <Input id="password" name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="h-12 bg-zinc-50 border-zinc-200 rounded-xl" required data-testid="login-password-input" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black font-bold text-base rounded-xl hover:shadow-lg hover:shadow-[#b8ff00]/30 transition-all" data-testid="login-submit-btn">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>

                <p className="text-center text-sm text-zinc-500 mt-8">
                  Don't have an account?{' '}
                  <Link to="/register" className="font-semibold text-zinc-900 hover:text-[#00aa55]" data-testid="register-link">Create one</Link>
                </p>
              </motion.div>
            ) : forgotMode === 'email' ? (
              <motion.div key="forgot-email" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}>
                <button onClick={() => setForgotMode(null)} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-6">
                  <ArrowLeft size={14} /> Back to sign in
                </button>
                <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <Mail size={24} className="text-amber-500" />
                </div>
                <h1 className="font-display font-bold text-2xl text-zinc-900 mb-2">Reset your password</h1>
                <p className="text-zinc-500 text-sm mb-6">Enter your email and we'll send you a 6-digit reset code.</p>
                <div className="space-y-4">
                  <Input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="you@example.com" className="h-12 bg-zinc-50 border-zinc-200 rounded-xl" data-testid="forgot-email-input" />
                  <Button onClick={handleForgotSubmit} disabled={resetLoading} className="w-full h-12 bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black font-bold rounded-xl" data-testid="send-reset-code-btn">
                    {resetLoading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                </div>
              </motion.div>
            ) : forgotMode === 'code' ? (
              <motion.div key="forgot-code" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}>
                <button onClick={() => setForgotMode('email')} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-6">
                  <ArrowLeft size={14} /> Back
                </button>
                <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
                  <KeyRound size={24} className="text-emerald-500" />
                </div>
                <h1 className="font-display font-bold text-2xl text-zinc-900 mb-2">Enter reset code</h1>
                <p className="text-zinc-500 text-sm mb-6">We sent a 6-digit code to <strong>{resetEmail}</strong></p>
                <div className="space-y-4">
                  <Input value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit code" maxLength={6} className="h-12 bg-zinc-50 border-zinc-200 rounded-xl text-center text-2xl tracking-[0.5em] font-mono" data-testid="reset-code-input" />
                  <div className="space-y-2">
                    <Label className="text-zinc-700">New Password</Label>
                    <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 6 characters" className="h-12 bg-zinc-50 border-zinc-200 rounded-xl" data-testid="new-password-input" />
                  </div>
                  <Button onClick={handleResetSubmit} disabled={resetLoading} className="w-full h-12 bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black font-bold rounded-xl" data-testid="reset-password-btn">
                    {resetLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                  <button onClick={handleForgotSubmit} className="w-full text-xs text-zinc-500 hover:text-zinc-900">
                    Didn't get the code? Send again
                  </button>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Right Side - Feature Showcase */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-zinc-900 to-zinc-800 items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="max-w-md text-white">
          <h2 className="font-display font-bold text-4xl mb-6">
            Join the <span className="gradient-text">fastest</span> marketplace
          </h2>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#b8ff00]/20 rounded-xl flex items-center justify-center flex-shrink-0"><Zap className="text-[#b8ff00]" size={24} /></div>
              <div><h3 className="font-semibold text-lg mb-1">Flash Deals</h3><p className="text-zinc-400 text-sm">Save up to 70% on limited-time offers that expire fast</p></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#00ff88]/20 rounded-xl flex items-center justify-center flex-shrink-0"><TrendingUp className="text-[#00ff88]" size={24} /></div>
              <div><h3 className="font-semibold text-lg mb-1">AI-Powered</h3><p className="text-zinc-400 text-sm">Smart recommendations that learn your preferences</p></div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center flex-shrink-0"><Shield className="text-violet-400" size={24} /></div>
              <div><h3 className="font-semibold text-lg mb-1">Secure Payments</h3><p className="text-zinc-400 text-sm">Stripe-powered checkout with buyer protection</p></div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
